import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import refreshAccessToken from '../utils/refreshAccessToken';

/** @typedef {'checking' | 'authenticated' | 'unauthenticated'} AuthStatus */

/**
 * @typedef {Object} AuthContextValue
 * @property {AuthStatus} status
 * @property {() => void} onAuthenticated - call after login sets its own tokens, to skip
 *   straight to authenticated without a reload (see core/Auth.js).
 */

/** @type {import('react').Context<AuthContextValue>} */
const AuthContext = createContext({
  status: 'checking',
  onAuthenticated: () => {},
});

const MAX_RETRIES = 1;
const RETRY_DELAY_MS = 1000;

/**
 * Bootstraps auth on mount: exchanges `@refresh_token` for an access token (never persisted, so
 * this must happen fresh every page load). Retries transient failures up to MAX_RETRIES; a
 * terminal failure (explicit rejection, or retries exhausted) clears `@refresh_token`.
 *
 * Must stay nested inside `<Checker>` (index.js) — its GetMeta/maintenance check has to resolve
 * before this bootstrap can fire any API call.
 *
 * @param {{ children: import('react').ReactNode }} props
 * @returns {JSX.Element}
 */
export function AuthProvider({ children }) {
  /** @type {[AuthStatus, (status: AuthStatus) => void]} */
  const [status, setStatus] = useState('checking');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Legacy key from before access tokens moved to in-memory storage — unread now, but purge it
    // here so existing logged-in users get it cleaned up on their next load, not just on logout.
    localStorage.removeItem('@access_token');

    if (!localStorage.getItem('@refresh_token')) {
      setStatus('unauthenticated');
      return undefined;
    }

    let cancelled = false;

    refreshAccessToken(null)
      .then(() => {
        if (!cancelled) setStatus('authenticated');
      })
      .catch((err) => {
        if (cancelled) return;

        const isTerminal = err?.transient === false || attempt + 1 >= MAX_RETRIES;

        if (isTerminal) {
          localStorage.removeItem('@refresh_token');
          setStatus('unauthenticated');
        } else {
          setTimeout(() => {
            if (!cancelled) setAttempt((a) => a + 1);
          }, RETRY_DELAY_MS);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  /** @type {AuthContextValue} */
  const value = useMemo(
    () => ({
      status,
      onAuthenticated: () => setStatus('authenticated'),
    }),
    [status],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * @returns {AuthContextValue}
 */
export const useAuthContext = () => useContext(AuthContext);

export default AuthContext;
